import {useEffect, useMemo, useRef, useCallback} from 'react';
import {FormField} from '../../../types/form-builder.types';
import {useFormSubmission} from '../../../contexts/form-submission-context';

interface CaptchaFieldProps {
  field: FormField;
  value?: any;
  onChange: (value: any) => void;
  error?: string;
}

/**
 * CAPTCHA Field for Form Submission
 *
 * Renders CAPTCHA widget on the public-facing form submission page.
 * Supports: reCAPTCHA Enterprise, hCaptcha, Cloudflare Turnstile
 */
export function CaptchaField({ field, onChange }: CaptchaFieldProps) {
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<any>(null);
  const isRenderedRef = useRef<boolean>(false);

  // Try to get form submission context - will be null in preview mode
  let formSubmissionContext;
  let isPreviewMode = false;
  try {
    formSubmissionContext = useFormSubmission();
    isPreviewMode = !formSubmissionContext;
  } catch (e) {
    // Context not available - we're in preview mode
    isPreviewMode = true;
    formSubmissionContext = null;
  }

  const integrations = formSubmissionContext?.integrations || [];

  // Auto-detect active CAPTCHA integration instead of relying on field config
  // Priority: field config > first active CAPTCHA integration
  const activeIntegration = useMemo(() => {
    if (isPreviewMode) {
      // In preview mode, return a mock integration
      return null;
    }

    // First, try to find integration matching field config
    const configuredType = (field.config as any)?.captchaType;
    if (configuredType) {
      const matchingIntegration = integrations.find(int =>
        int.provider === configuredType && int.status === 'active'
      );
      if (matchingIntegration) {
        return matchingIntegration;
      }
    }

    // Fallback: Find any active CAPTCHA integration
    const captchaProviders = ['recaptcha-enterprise', 'hcaptcha', 'turnstile'];
    return integrations.find(int =>
      captchaProviders.includes(int.provider) && int.status === 'active'
    );
  }, [integrations, field.config, isPreviewMode]);

  // Get captchaType from active integration
  const captchaType = activeIntegration?.provider || 'recaptcha-enterprise';

  // Get siteKey from integration config
  const siteKey = useMemo(() => {
    if (activeIntegration && activeIntegration.config) {
      const key = activeIntegration.config.site_key || '';
      console.log('[CAPTCHA Field] Site key extracted:', {
        provider: activeIntegration.provider,
        hasSiteKey: !!key,
        configKeys: Object.keys(activeIntegration.config)
      });
      return key;
    }
    console.warn('[CAPTCHA Field] No active integration found');
    return '';
  }, [activeIntegration]);

  const theme = (field.config as any)?.theme || activeIntegration?.config?.theme || 'light';
  const size = (field.config as any)?.size || activeIntegration?.config?.size || 'normal';
  const action = (field.config as any)?.action || activeIntegration?.config?.action || 'submit';

  // Safe onChange wrapper to prevent side effects
  const handleChange = useCallback((value: any) => {
    // Call onChange directly
    try {
      onChange(value);
    } catch (error) {
      console.error('[CAPTCHA] Error in onChange:', error);
    }
  }, [onChange]);

  useEffect(() => {
    if (!siteKey) {
      console.warn('CAPTCHA site key not configured');
      return;
    }

    // Load and render appropriate CAPTCHA
    switch (captchaType) {
      case 'recaptcha-enterprise':
        loadRecaptchaEnterprise();
        break;
      case 'hcaptcha':
        loadHcaptcha();
        break;
      case 'turnstile':
        loadTurnstile();
        break;
    }

    return () => {
      // Cleanup widget on unmount
      if (widgetIdRef.current !== null) {
        try {
          switch (captchaType) {
            case 'hcaptcha':
              // Check if hcaptcha API is available and widget exists
              if ((window as any).hcaptcha?.reset && (window as any).hcaptcha?.getRespKey) {
                try {
                  // Verify widget still exists before resetting
                  (window as any).hcaptcha.getRespKey(widgetIdRef.current);
                  (window as any).hcaptcha.reset(widgetIdRef.current);
                } catch (e) {
                  // Widget already destroyed or invalid, ignore
                }
              }
              break;
            case 'turnstile':
              // Check if turnstile API is available
              if ((window as any).turnstile?.reset && (window as any).turnstile?.getResponse) {
                try {
                  // Verify widget still exists before resetting
                  (window as any).turnstile.getResponse(widgetIdRef.current);
                  (window as any).turnstile.reset(widgetIdRef.current);
                } catch (e) {
                  // Widget already destroyed or invalid, ignore
                }
              }
              break;
          }
        } catch (e) {
          // Silently ignore cleanup errors - widget may have been destroyed already
          // This is normal during form submission when components unmount
        }
      }

      // Clear refs
      widgetIdRef.current = null;
      isRenderedRef.current = false;
    };
  }, [siteKey, captchaType, theme, size, action]);

  const loadRecaptchaEnterprise = () => {
    if (!(window as any).grecaptcha || !(window as any).grecaptcha.enterprise) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    // Store site key and action for form submission
    (window as any).__recaptchaEnterpriseSiteKey = siteKey;
    (window as any).__recaptchaEnterpriseAction = action;
  };

  const loadHcaptcha = () => {
    if (!(window as any).hcaptcha) {
      const script = document.createElement('script');
      script.src = 'https://js.hcaptcha.com/1/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => renderHcaptcha();
      document.head.appendChild(script);
    } else {
      renderHcaptcha();
    }
  };

  const renderHcaptcha = () => {
    if (!captchaRef.current) return;
    if (isRenderedRef.current) return; // Prevent re-rendering

    const hcaptcha = (window as any).hcaptcha;
    if (!hcaptcha?.render) {
      setTimeout(renderHcaptcha, 100);
      return;
    }

    try {
      widgetIdRef.current = hcaptcha.render(captchaRef.current, {
        sitekey: siteKey,
        theme: theme,
        size: size,
        callback: (response: string) => {
          console.log('[hCaptcha] Token received, length:', response?.length);
          handleChange(response);
        },
        'expired-callback': () => {
          console.log('[hCaptcha] Token expired');
          handleChange(null);
        },
        'error-callback': () => {
          console.error('[hCaptcha] Error occurred');
          handleChange(null);
        },
      });

      isRenderedRef.current = true;
      console.log('[hCaptcha] Widget rendered successfully', widgetIdRef.current);
    } catch (e) {
      console.error('hCaptcha render error:', e);
    }
  };

  const loadTurnstile = () => {
    if (!(window as any).turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => renderTurnstile();
      document.head.appendChild(script);
    } else {
      renderTurnstile();
    }
  };

  const renderTurnstile = () => {
    if (!captchaRef.current) return;
    if (isRenderedRef.current) return; // Prevent re-rendering

    const turnstile = (window as any).turnstile;
    if (!turnstile?.render) {
      setTimeout(renderTurnstile, 100);
      return;
    }

    try {
      widgetIdRef.current = turnstile.render(captchaRef.current, {
        sitekey: siteKey,
        theme: theme,
        size: size,
        callback: (response: string) => {
          console.log('[Turnstile] Token received, length:', response?.length);
          handleChange(response);
        },
        'expired-callback': () => {
          console.log('[Turnstile] Token expired');
          handleChange(null);
        },
        'error-callback': () => {
          console.error('[Turnstile] Error occurred');
          handleChange(null);
        },
      });

      isRenderedRef.current = true;
      console.log('[Turnstile] Widget rendered successfully', widgetIdRef.current);
    } catch (e) {
      console.error('Turnstile render error:', e);
    }
  };

  if (!siteKey) {
    // In preview mode, show a placeholder
    if (isPreviewMode) {
      const configuredType = (field.config as any)?.captchaType || 'recaptcha-enterprise';
      const displayName = configuredType === 'hcaptcha' ? 'hCaptcha'
        : configuredType === 'turnstile' ? 'Cloudflare Turnstile'
        : 'reCAPTCHA Enterprise';

      return (
        <div className="p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-md text-center">
          <div className="flex items-center justify-center gap-2 text-gray-600 mb-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="font-medium">{displayName} Widget</span>
          </div>
          <p className="text-xs text-gray-500">CAPTCHA widget will appear here on the live form</p>
        </div>
      );
    }

    // On live form, show error if no integration is configured
    const configuredType = (field.config as any)?.captchaType;
    const message = configuredType
      ? `No active ${configuredType} integration found.`
      : 'No active reCAPTCHA Enterprise integration found.';

    return (
      <div className="text-sm text-red-600 p-3 bg-red-50 rounded-md border border-red-200">
        <p className="font-medium mb-1">{message}</p>
        <p className="text-xs">
          Please go to <strong>Integrations → Security & CAPTCHA</strong> and configure a {configuredType || 'reCAPTCHA Enterprise'} integration first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {captchaType === 'recaptcha-enterprise' ? (
        <div className="text-xs text-muted-foreground p-2 bg-muted/30 rounded-md border border-border/50 flex items-center gap-2">
          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span>
            This form is protected by reCAPTCHA Enterprise. Verification happens automatically.
          </span>
        </div>
      ) : (
        <div
          ref={captchaRef}
          className="captcha-widget"
          onClick={(e) => e.stopPropagation()}
          onSubmit={(e) => e.preventDefault()}
        />
      )}

      {/* Error display removed - handled by FormFieldRenderer wrapper */}

      {(field.config as any)?.showPrivacyNote && (
        <div className="text-xs text-muted-foreground">
          This site is protected by CAPTCHA and subject to applicable privacy policies and terms of service.
        </div>
      )}
    </div>
  );
}

